import type { Thing } from "schema-dts";
import { BLOCK_STRUCTURED_DATA_BY_TYPE } from "../blocks";
import type { SchemaBuilder } from "../types";

const ID_KEY = "@id";
const TYPE_KEY = "@type";
const BREADCRUMB = "BreadcrumbList";

/** Assinatura do nó ignorando o `@id`, que é o que distingue as instâncias. */
function contentKey(node: Thing): string {
  const { [ID_KEY]: _id, ...content } = node as Record<string, unknown>;
  return JSON.stringify(content);
}

/**
 * Dois blocos de breadcrumb com exatamente a mesma sequência viram um nó só.
 *
 * Vale **apenas para `BreadcrumbList`**: nó de outro tipo passa intacto, e cada
 * tipo novo define a própria regra de duplicidade.
 *
 * A comparação é por **conteúdo**, ignorando o `@id` — ele carrega o bloco, e
 * por isso dois blocos nunca são iguais por ele. Vence o primeiro, de modo que
 * a ordem da página decide.
 *
 * Caminhos diferentes continuam saindo como listas separadas, cada uma com a
 * sua ordem e o seu identificador.
 */
function dropDuplicateTrails(nodes: Thing[]): Thing[] {
  const seen = new Set<string>();

  return nodes.filter((node) => {
    if ((node as Record<string, unknown>)[TYPE_KEY] !== BREADCRUMB) return true;

    const key = contentKey(node);
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

/**
 * Nós declarados pelos próprios blocos da página.
 *
 * A agregação continua sendo do grafo: o builder só despacha pelo registry e
 * concatena, e nenhum bloco precisa conhecer o `@graph` nem os demais nós. Fica
 * por último em `BUILDERS` — na consolidação por `@id`, quem chega antes tem a
 * palavra final, e a identidade do site não se negocia com um bloco.
 */
export const buildBlockNodes: SchemaBuilder = (context) =>
  dropDuplicateTrails(
    context.blockSources.flatMap((source) => {
      const resolve = BLOCK_STRUCTURED_DATA_BY_TYPE[source.typeName];
      if (!resolve) return [];

      return resolve({ ...source, context });
    }),
  );
